import { remainingMs, isOvertime, formatClock } from '../lib/rest-timer.js';
import { icon } from './icons.js';

let state = null; // { endTs, totalMs, label, next, fired, raf, els }
let audioCtx = null;
let pos = null;   // { x, y } drag offset, persists across rests this session

function ensureAudio() {
  try {
    audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    if (audioCtx.state === 'suspended') audioCtx.resume();
  } catch { /* no audio */ }
}

function beep() {
  if (!audioCtx) return;
  try {
    const o = audioCtx.createOscillator();
    const g = audioCtx.createGain();
    o.connect(g); g.connect(audioCtx.destination);
    o.frequency.value = 880; o.type = 'sine';
    g.gain.setValueAtTime(0.001, audioCtx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.4, audioCtx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.5);
    o.start(); o.stop(audioCtx.currentTime + 0.5);
  } catch { /* ignore */ }
}

function container() {
  let n = document.getElementById('grit-rest-timer');
  if (!n) {
    n = document.createElement('div');
    n.id = 'grit-rest-timer';
    n.style.position = 'fixed';
    n.style.left = '12px';
    n.style.right = '12px';
    n.style.bottom = '96px';
    n.style.zIndex = '90';
    n.style.maxWidth = '28rem';
    n.style.margin = '0 auto';
    document.body.appendChild(n);
  }
  return n;
}

// build the DOM once; return element refs we update on tick
function build() {
  const n = container();
  n.innerHTML = `
    <div data-drag class="bg-header text-white rounded-2xl px-3.5 py-3 flex items-center gap-3 select-none" style="touch-action:none">
      <div data-ring class="w-11 h-11 rounded-full flex items-center justify-center text-[13px] font-bold flex-none">
        <span data-clock class="bg-header rounded-full w-9 h-9 flex items-center justify-center">0:00</span>
      </div>
      <div class="flex-1 min-w-0">
        <div data-title class="text-[13px] font-bold truncate">Resting</div>
        <div data-next class="text-[12px] text-white/60 truncate"></div>
      </div>
      <button data-add class="text-[13px] font-bold bg-white/15 rounded-lg px-3 py-2.5 active:scale-95">+30</button>
      <button data-skip class="text-[13px] font-bold bg-grit rounded-lg px-3 py-2.5 active:scale-95">Skip</button>
      <button data-close aria-label="Dismiss" class="text-white/70 w-7 h-7 flex items-center justify-center active:scale-95">${icon.x('w-4 h-4')}</button>
    </div>`;
  const els = {
    ring: n.querySelector('[data-ring]'),
    clock: n.querySelector('[data-clock]'),
    title: n.querySelector('[data-title]'),
    next: n.querySelector('[data-next]'),
  };
  n.querySelector('[data-add]').onclick = () => { if (state) { state.endTs += 30000; state.totalMs += 30000; state.fired = false; render(); } };
  n.querySelector('[data-skip]').onclick = () => stopRest();
  n.querySelector('[data-close]').onclick = () => stopRest();
  enableDrag(n.querySelector('[data-drag]'), n);
  applyPos(n);
  return els;
}

function applyPos(n) { if (pos) n.style.transform = `translate(${pos.x}px, ${pos.y}px)`; }

function enableDrag(handle, n) {
  let start = null;
  handle.addEventListener('pointerdown', (e) => {
    if (e.target.closest('button')) return; // don't drag when tapping a control
    start = { px: e.clientX, py: e.clientY, x: pos?.x || 0, y: pos?.y || 0 };
    handle.setPointerCapture(e.pointerId);
  });
  handle.addEventListener('pointermove', (e) => {
    if (!start) return;
    pos = { x: start.x + (e.clientX - start.px), y: start.y + (e.clientY - start.py) };
    applyPos(n);
  });
  const end = () => { start = null; };
  handle.addEventListener('pointerup', end);
  handle.addEventListener('pointercancel', end);
}

// update only the changing bits (cheap; called each frame)
function render() {
  if (!state) return;
  const now = Date.now();
  const over = isOvertime(state.endTs, now);
  const ringColor = over ? '#15A36B' : 'rgb(var(--brand))';
  const frac = over ? 1 : remainingMs(state.endTs, now) / state.totalMs;
  state.els.ring.style.background = `conic-gradient(${ringColor} ${Math.round(frac * 360)}deg, rgba(255,255,255,.15) 0)`;
  state.els.clock.textContent = over ? '+' + formatClock(now - state.endTs) : formatClock(remainingMs(state.endTs, now));
  state.els.title.textContent = over ? (state.doneLabel || "Rest's up, go") : (state.title || 'Resting · ' + state.label);
  state.els.next.textContent = state.next ? 'Next: ' + state.next : '';
}

function tick() {
  if (!state) return;
  const now = Date.now();
  if (!state.fired && now >= state.endTs) {
    state.fired = true;
    if (localStorage.getItem('grit-pref-sound') !== '0') beep();
    if (localStorage.getItem('grit-pref-vibration') !== '0') navigator.vibrate?.([200, 100, 200]);
  }
  render();
  state.raf = requestAnimationFrame(tick);
}

export function startRest({ label, next, seconds, title = null, doneLabel = null }) {
  ensureAudio();
  if (state?.raf) cancelAnimationFrame(state.raf);
  const totalMs = seconds * 1000;
  const els = build();
  state = { endTs: Date.now() + totalMs, totalMs, label, next, title, doneLabel, fired: false, raf: null, els };
  localStorage.setItem('grit-rest', JSON.stringify({ endTs: state.endTs }));
  navigator.serviceWorker?.controller?.postMessage({ type: 'schedule-rest', endTs: state.endTs, body: next || 'Next set.' });
  render();
  state.raf = requestAnimationFrame(tick);
}

export function stopRest() {
  if (state?.raf) cancelAnimationFrame(state.raf);
  state = null;
  localStorage.removeItem('grit-rest');
  const n = document.getElementById('grit-rest-timer');
  if (n) n.innerHTML = '';
}

window.addEventListener('visibilitychange', () => { if (state) render(); });
